// @phase13-shim — replaces 630-line legacy body after P5 cutover.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DesignerShellPage } from '../../designer';
import SlideDeckPlayer from '../../components/slide_deck/SlideDeckPlayer';
import { useSceneStore } from '../../store/sceneStore';
import VersionHistoryPanel from '../../components/versioning/VersionHistoryPanel';

function SlideDeckCanvasSlot({ sceneId }) {
  const scene = useSceneStore((s) => s.scene);
  const meta = scene?.domain_metadata || {};
  const slides = meta.slides || scene?.slides;
  const hasDeck = Boolean(meta.deck_id || scene?.deck_id);
  const hasInline = Array.isArray(slides) && slides.length > 0;

  return (
    <div>
      {hasDeck ? (
        <SlideDeckPlayer sceneId={sceneId} deckId={meta.deck_id || scene.deck_id} />
      ) : hasInline ? (
        <InlineSlidePreview slides={slides} />
      ) : !scene ? (
        <div style={loadingStyle}>Loading scene…</div>
      ) : (
        <div style={loadingStyle}>No slides uploaded or authored yet.</div>
      )}
    </div>
  );
}

function InlineSlidePreview({ slides, compact = false }) {
  const [idx, setIdx] = React.useState(0);
  const [showNotes, setShowNotes] = React.useState(false);
  const total = slides.length;
  const slide = slides[idx] || {};

  React.useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(total - 1, i + 1));
      else if (e.key === 's' || e.key === 'S') setShowNotes((v) => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [total]);

  const bullets = slide.bullets || slide.bullet_points || [];
  const bodyText = slide.body || slide.content || '';
  const notesText = slide.speaker_notes || slide.notes || '';
  const imageUrl = slide.image_url || '';

  return (
    <div style={{ border: '1px solid #334155', borderRadius: compact ? 8 : 12, overflow: 'hidden', background: '#0f172a' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc',
          padding: compact ? '20px 24px' : '32px 36px',
          minHeight: compact ? 200 : 360,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: bullets.length > 0 || bodyText ? 'flex-start' : 'center',
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: 1.5, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 14, display: 'flex', justifyContent: 'space-between' }}>
          <span>Slide {idx + 1} of {total}</span>
        </div>
        <h2 style={{ fontSize: compact ? 20 : 26, margin: '0 0 18px', lineHeight: 1.25, color: '#f8fafc' }}>
          {slide.title || `Slide ${idx + 1}`}
        </h2>

        {imageUrl && (
          <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
            <img
              src={imageUrl}
              alt={slide.title || 'Slide image'}
              style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: 8, maxHeight: compact ? 200 : 400 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {bullets.length > 0 && (
          <ul style={{ fontSize: compact ? 14 : 16, lineHeight: 1.7, paddingLeft: 22, margin: 0, color: '#e2e8f0' }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{b}</li>
            ))}
          </ul>
        )}
        {bodyText && (
          <p style={{ fontSize: compact ? 13 : 15, lineHeight: 1.7, marginTop: 12, color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>
            {bodyText}
          </p>
        )}
      </div>

      {notesText && showNotes && (
        <div style={{ background: '#fef3c7', color: '#78350f', padding: '12px 20px', fontSize: 13, lineHeight: 1.6, borderBottom: '1px solid #fde68a' }}>
          <strong style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            Speaker notes
          </strong>
          <span style={{ whiteSpace: 'pre-wrap' }}>{notesText}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#1e293b' }}>
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} style={navBtn(idx === 0)}>← Prev</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {notesText && (
            <button onClick={() => setShowNotes((v) => !v)} style={navBtn(false)}>
              {showNotes ? 'Hide notes' : 'Show notes'}
            </button>
          )}
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{idx + 1} / {total}</span>
        </div>
        <button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} disabled={idx >= total - 1} style={navBtn(idx >= total - 1)}>Next →</button>
      </div>

      {/* Slide thumbnail strip */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 12px', background: '#0f172a', overflowX: 'auto' }}>
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            style={{
              flex: '0 0 auto',
              width: 48,
              height: 32,
              borderRadius: 4,
              border: i === idx ? '2px solid #3b82f6' : '1px solid #334155',
              background: i === idx ? '#1e3a5f' : '#1e293b',
              color: '#94a3b8',
              fontSize: 10,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            #{i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}

const loadingStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: 300, color: '#94a3b8', fontSize: 15,
};

function navBtn(disabled) {
  return {
    background: disabled ? '#334155' : '#475569',
    color: disabled ? '#64748b' : '#f8fafc',
    border: 'none',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

const versionBtnStyle = {
  background: '#1e293b',
  color: '#94a3b8',
  border: '1px solid #334155',
  borderRadius: 6,
  padding: '5px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

export default function SlideDeckSceneDesignerPage() {
  const { sceneId } = useParams();
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '6px 16px',
        background: '#0f172a',
        borderBottom: '1px solid #1e293b',
        flexShrink: 0,
      }}>
        <button style={versionBtnStyle} onClick={() => setVersionHistoryOpen(true)}>
          Version History
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DesignerShellPage
          sceneId={sceneId}
          canvasSlot={<SlideDeckCanvasSlot sceneId={sceneId} />}
          userRoles={[]}
          appSurface="coach"
        />
      </div>
      <VersionHistoryPanel
        sceneId={sceneId}
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        onRestore={() => window.location.reload()}
      />
    </div>
  );
}
